import { Component, effect, inject, OnDestroy, OnInit } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { PrimeNG } from "primeng/config";
import { EMPTY, Subscription } from "rxjs";
import { catchError, filter, map, switchMap, tap } from "rxjs/operators";

import { CURRENT_LOCALE_ID } from "@intocare/angular-data/lib/current-locale-id";
import { AuthModel, InactivityService, PermissionsService } from "@intocare/auth-data";
import { AWSAuthService } from "@intocare/aws";
import { ChangelogService, MatomoService } from "@intocare/core";
import { providePrimengLocale } from "@intocare/design/design-system-core";
import { ENVIRONMENT_TOKEN } from "@intocare/environments";
import { SnackbarService } from "@intocare/ui";
import { CONTEXT_TOKEN, ContextNotInitializedError } from "@intocare/util";

@Component({
	selector: "ic-app-root",
	templateUrl: "./app.component.html",
	styleUrls: ["./app.component.scss"],
	standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
	private subscriptions: Subscription[] = [];

	private readonly router = inject(Router);
	private readonly awsAuthService = inject(AWSAuthService);
	private readonly inactivityService = inject(InactivityService);
	private readonly authModel = inject(AuthModel);
	private readonly swUpdate = inject(SwUpdate);
	private readonly snackbarService = inject(SnackbarService);
	private readonly changelogService = inject(ChangelogService);
	private readonly primeNG = inject(PrimeNG);
	private readonly matomoService = inject(MatomoService);
	private readonly environment = inject(ENVIRONMENT_TOKEN);
	private readonly contextService = inject(CONTEXT_TOKEN);
	private readonly permissionsService = inject(PermissionsService);
	private readonly currentLocaleId = inject(CURRENT_LOCALE_ID);

	private readonly context$ = toObservable(this.contextService.context);

	constructor() {
		effect(() => {
			if (this.awsAuthService.cognitoUser()) {
				this.inactivityService.bootstrap(this.environment.INACTIVITY_TIMEOUT || 300);
			}
		});
		effect(() => {
			providePrimengLocale(this.primeNG, this.currentLocaleId());
		});
	}

	ngOnInit() {
		this.matomoService.load();

		const inactivitySubscription = this.inactivityService.subscribe(() => {
			this.router.navigate([""]).then(() => {
				this.logOut(true);
			});
		});

		this.awsAuthService.onRefreshError(() => {
			this.logOut();
		});

		const contextSubscription = this.context$
			.pipe(
				map((context) => {
					try {
						return { acceptedTermsAndConditions: context.acceptedTermsAndConditions };
					} catch (error) {
						if (error instanceof ContextNotInitializedError) {
							return null;
						}

						throw error;
					}
				}),
				filter((context) => context !== null),
				switchMap((context) => {
					if (context.acceptedTermsAndConditions) {
						return this.changelogService.loadChangelog();
					}

					return EMPTY;
				}),
			)
			.subscribe();

		const swSubscription = this.swUpdate.versionUpdates
			.pipe(
				filter((evt): evt is VersionReadyEvent => evt.type === "VERSION_READY"),
				switchMap(() => {
					const snackbar = this.snackbarService.show({
						text: "general.updates_available",
						action: "general.apply_updates",
						autoDismiss: false,
					});

					return snackbar.instance.action.pipe(
						tap(() => {
							document.location.reload();
							snackbar.close();
						}),
					);
				}),
			)
			.subscribe();

		const permissionsSubscription = this.permissionsService.handleClearingVisitedUrls();

		this.subscriptions.push(swSubscription, contextSubscription, inactivitySubscription, permissionsSubscription);
	}

	ngOnDestroy() {
		for (const subscription of this.subscriptions) {
			subscription.unsubscribe();
		}
	}

	/**
	 * Log out and navigate to the auth page.
	 *
	 * @param inactivity To be set to 'true' when called due to failing refresh.
	 */
	private logOut(inactivity?: boolean): void {
		this.authModel
			.logOut(inactivity)
			.pipe(
				tap((redirectUrl) => {
					if (redirectUrl) {
						document.location.replace(redirectUrl);
					} else {
						this.router.navigate(["select-organisation"]).then(() => {
							document.location.reload();
						});
					}
				}),
				catchError(() => {
					this.router.navigate(["select-organisation"]).then(() => {
						document.location.reload();
					});
					return EMPTY;
				}),
			)
			.subscribe();
	}
}
